import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { useToast } from '../components/ui/toast';
import { Trash2 } from 'lucide-react';

interface UserSettings {
  first_name: string;
  last_name: string;
  avatar_url: string;
  timezone: string;
  language: string;
  theme: string;
}

interface SettingsData {
  user: {
    id: number;
    email: string;
    name: string;
    avatar_url?: string;
  };
  settings: UserSettings;
  subscription: {
    id: number;
    plan_id: number;
    status: string;
    plan: {
      name: string;
      price: number;
      currency: string;
      features: string[];
    };
  };
  api_keys: Array<{
    id: number;
    service: string;
    key_name: string;
    is_active: boolean;
  }>;
  available_plans: Array<{
    id: number;
    name: string;
    description: string;
    price: number;
    currency: string;
    features: string[];
  }>;
}

const Settings: React.FC = () => {
  const [settingsData, setSettingsData] = useState<SettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Confirmation dialog states
  const [showPlanUpgradeDialog, setShowPlanUpgradeDialog] = useState(false);
  const [showApiKeyDeleteDialog, setShowApiKeyDeleteDialog] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<number | null>(null);
  
  const { showToast } = useToast();
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    avatar_url: '',
    timezone: 'UTC',
    language: 'en',
    theme: 'auto'
  });
  const [newAPIKey, setNewAPIKey] = useState({
    service: '',
    key_name: '',
    key_value: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const responseData = await response.json();
        const data = responseData.data; // Extract the actual data from the response
        setSettingsData(data);
        
        // Populate form with existing data
        if (data.settings) {
          setProfileForm({
            first_name: data.settings.first_name || '',
            last_name: data.settings.last_name || '',
            avatar_url: data.settings.avatar_url || '',
            timezone: data.settings.timezone || 'UTC',
            language: data.settings.language || 'en',
            theme: data.settings.theme || 'auto'
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileForm)
      });
      
      if (response.ok) {
        showToast('Profile updated successfully!', 'success');
        fetchSettings(); // Refresh data
      } else {
        showToast('Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      showToast('Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addAPIKey = async () => {
    if (!newAPIKey.service || !newAPIKey.key_name || !newAPIKey.key_value) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newAPIKey)
      });
      
      if (response.ok) {
        showToast('API key added successfully!', 'success');
        setNewAPIKey({ service: '', key_name: '', key_value: '' });
        fetchSettings(); // Refresh data
      } else {
        showToast('Failed to add API key', 'error');
      }
    } catch (error) {
      console.error('Failed to add API key:', error);
      showToast('Failed to add API key', 'error');
    }
  };

  const deleteAPIKey = (keyId: number) => {
    setSelectedApiKeyId(keyId);
    setShowApiKeyDeleteDialog(true);
  };

  const confirmDeleteAPIKey = async () => {
    if (!selectedApiKeyId) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings/api-keys/${selectedApiKeyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        showToast('API key deleted successfully!', 'success');
        fetchSettings(); // Refresh data
      } else {
        showToast('Failed to delete API key', 'error');
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
      showToast('Failed to delete API key', 'error');
    }
  };

  const handlePlanUpgrade = (planId: number) => {
    setSelectedPlanId(planId);
    setShowPlanUpgradeDialog(true);
  };

  const confirmPlanUpgrade = async () => {
    if (!selectedPlanId) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: selectedPlanId,
          auto_renew: true,
          payment_method: 'credit_card' // Default payment method
        })
      });
      
      if (response.ok) {
        showToast('Subscription plan updated successfully!', 'success');
        fetchSettings(); // Refresh data
      } else {
        const errorData = await response.json();
        showToast(`Failed to update subscription: ${errorData.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Failed to update subscription:', error);
      showToast('Failed to update subscription', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings and preferences</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'subscription'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Subscription
          </button>
          <button
            onClick={() => setActiveTab('api-keys')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'api-keys'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            API Keys
          </button>
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  First Name
                </label>
                <Input
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Last Name
                </label>
                <Input
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  placeholder="Enter your last name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Avatar URL
              </label>
              <Input
                value={profileForm.avatar_url}
                onChange={(e) => setProfileForm({ ...profileForm, avatar_url: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Timezone
                </label>
                <Select
                  value={profileForm.timezone}
                  onValueChange={(value) => setProfileForm({ ...profileForm, timezone: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">America/New_York</SelectItem>
                    <SelectItem value="Europe/London">Europe/London</SelectItem>
                    <SelectItem value="Asia/Tokyo">Asia/Tokyo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Language
                </label>
                <Select
                  value={profileForm.language}
                  onValueChange={(value) => setProfileForm({ ...profileForm, language: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Theme
                </label>
                <Select
                  value={profileForm.theme}
                  onValueChange={(value) => setProfileForm({ ...profileForm, theme: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-4">
              <Button onClick={updateProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <Card>
          <CardHeader>
            <CardTitle>Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            {settingsData?.subscription ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{settingsData.subscription.plan.name}</h3>
                    <p className="text-muted-foreground">
                      ${settingsData.subscription.plan.price} {settingsData.subscription.plan.currency}
                    </p>
                  </div>
                  <Badge variant={settingsData.subscription.status === 'active' ? 'default' : 'secondary'}>
                    {settingsData.subscription.status}
                  </Badge>
                </div>
                
                {/* Current Plan Features */}
                {settingsData.subscription.plan.features && settingsData.subscription.plan.features.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2">Your Plan Features</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {settingsData.subscription.plan.features.map((feature, index) => (
                        <li key={index} className="text-sm text-muted-foreground">
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="pt-4">
                  <h4 className="font-medium mb-2">Available Plans</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {settingsData.available_plans?.map((plan) => (
                      <Card key={plan.id} className="p-4">
                        <h5 className="font-semibold">{plan.name}</h5>
                        <p className="text-sm text-muted-foreground">{plan.description}</p>
                        <p className="text-lg font-bold mt-2">
                          ${plan.price} {plan.currency}
                        </p>
                        
                        {/* Plan Features */}
                        {plan.features && plan.features.length > 0 && (
                          <div className="mt-3">
                            <h6 className="text-sm font-medium mb-2">Features:</h6>
                            <ul className="list-disc list-inside space-y-1">
                              {plan.features.map((feature, index) => (
                                <li key={index} className="text-xs text-muted-foreground">
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <Button 
                          size="sm" 
                          className="w-full mt-3"
                          disabled={plan.id === settingsData.subscription.plan_id}
                          onClick={() => handlePlanUpgrade(plan.id)}
                        >
                          {plan.id === settingsData.subscription.plan_id ? 'Current Plan' : 
                           plan.price > settingsData.subscription.plan.price ? 'Upgrade' : 'Downgrade'}
                        </Button>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">No subscription information available</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Add New API Key</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Service
                  </label>
                  <Select
                    value={newAPIKey.service}
                    onValueChange={(value) => setNewAPIKey({ ...newAPIKey, service: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini">Gemini</SelectItem>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Key Name
                  </label>
                  <Input
                    value={newAPIKey.key_name}
                    onChange={(e) => setNewAPIKey({ ...newAPIKey, key_name: e.target.value })}
                    placeholder="My API Key"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Key Value
                  </label>
                  <Input
                    type="password"
                    value={newAPIKey.key_value}
                    onChange={(e) => setNewAPIKey({ ...newAPIKey, key_value: e.target.value })}
                    placeholder="sk-..."
                  />
                </div>
              </div>
              
              <Button onClick={addAPIKey}>
                Add API Key
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your API Keys</CardTitle>
            </CardHeader>
            <CardContent>
              {settingsData?.api_keys && settingsData.api_keys.length > 0 ? (
                <div className="space-y-3">
                  {settingsData.api_keys.map((key) => (
                    <div key={key.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium">{key.key_name}</p>
                        <p className="text-sm text-muted-foreground">{key.service}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={key.is_active ? 'default' : 'secondary'}>
                          {key.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteAPIKey(key.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No API keys configured</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={showPlanUpgradeDialog}
        onOpenChange={setShowPlanUpgradeDialog}
        title="Change Subscription Plan"
        description="Are you sure you want to change your subscription plan? This action will update your billing and plan features immediately."
        confirmText="Change Plan"
        cancelText="Cancel"
        onConfirm={confirmPlanUpgrade}
        variant="default"
      />

      <ConfirmationDialog
        open={showApiKeyDeleteDialog}
        onOpenChange={setShowApiKeyDeleteDialog}
        title="Delete API Key"
        description="Are you sure you want to delete this API key? This action cannot be undone and may affect your Agent Shopper functionality."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteAPIKey}
        variant="destructive"
      />
    </div>
  );
};

export { Settings };
export default Settings;
